import { authHeaders, Axios } from "@/utils/axios";
import { API_END_POINTS } from "@/utils/routes";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface JobProfile {
  _id: string;
  title: string;
  description: string;
  category: string;
  requiredSkills: string[];
  budget: number;
  pricingModel?: string | "dailyRate" | "fixedPrice";
  PricingDetails?: string; // align with components using PricingDetails
  duration?: string;
  durationType?: string;
  durationValue?: number;
  durationUnit?: number;
  applicationCount?: number;
  projectvisibility?: boolean;
  otherRequirements?: string[];
  createdAt: string;
  updatedAt: string;
  experienceLevel?: string;
  enterpriseId?: {
    contactPerson: {
      name: string;
      email: string;
      phone: string;
    };
    location: {
      address: string;
      city: string;
      country: string;
    };
    _id: string;
    userId: string;
    companyName: string;
    industryType: string;
    preferredCurrency: string;
    preferredPaymentMethod: string;
    vatOrGstId: string | number;
    socialMediaProfile: any;
    rating: number;
    previousProjects: string[];
    createdAt: string;
    updatedAt: string;
  };
}

interface JobApply {
  jobId: string;
  freelancerId: string;
  userId: string;
  coverLetterText: string;
  coverLetterFileUrl: string;
  proposedBudget: string;
}
interface AllJobProps {
  search: string;
  skills: string;
  category: string;
  budget: string;
  PricingDetails: string;
  sortBy: string;
  order: string;
  page: number;
  limit: number;
}

interface JobState {
  profile: {
    results: any;
    jobs: JobProfile[] | null;
    total: number;
    page: number;
    totalPages: number;
  };
  userJobs: JobProfile[] | null;
  isLoading: boolean;
  error: string | null;
  fetchAllJobProfile: (
    data: Partial<AllJobProps>,
    token: string
  ) => Promise<void>;
  fetchAllProject: (
    id: string,
    token: string,
    params?: {
      skills?: string;
      page?: number;
      limit?: number;
    }
  ) => Promise<void>;
  fetchJobProfileById: (id: string, token: string) => Promise<void>;
  createJobProfile: (data: Partial<JobProfile>, token: string) => Promise<void>;
  updateJobProfile: (
    id: string,
    data: Partial<JobProfile>,
    token: string
  ) => Promise<void>;
  applyJob: (
    id: string,
    data: Partial<JobApply>,
    token: string
  ) => Promise<void>;
  clearJobProfile: () => void;
  updateJobStatus: (
    id:string,
    data: boolean,
    token: string
  ) => Promise<void>;
  deleteJob: (
    id: string,
    token: string
  ) => Promise<void>;
}

export const useJobStore = create<JobState>()(
  persist(
    (set) => ({
      profile: null,
      userJobs: null,
      isLoading: false,
      error: null,

      fetchAllJobProfile: async (data, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(
            `${API_END_POINTS.GET_ALL_JOBS}?search=${data.search}&skills=${data.skills}&category=${data.category}&budget=${data.budget}&PricingDetails=${data.PricingDetails}&sortBy=${data.sortBy}&order=${data.order}&page=${data.page}&limit=${data.limit}`,
            authHeaders(token)
          );
          set({ profile: res.data, isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to fetch job profile",
            isLoading: false,
          });
        }
      },

      fetchAllProject: async (id, token, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(
            `${API_END_POINTS.GET_ALL_PROJECTS}/${id}`,
            {
              ...authHeaders(token),
              params,
            }
          );
          set({ profile: res.data, isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to fetch job profile",
            isLoading: false,
          });
        }
      },
      fetchJobProfileById: async (id, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(
            `${API_END_POINTS.GET_JOB_DETAILS}/${id}`,
            authHeaders(token)
          );
          set({ userJobs: res.data, isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to fetch job profile",
            isLoading: false,
          });
        }
      },

      createJobProfile: async (updatedData, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.post(
            `${API_END_POINTS.CREATE_JOB}`,
            updatedData,
            authHeaders(token)
          );
          set({ profile: res.data, isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to update job profile",
            isLoading: false,
          });
          throw err;
        }
      },

      updateJobProfile: async (id, updatedData, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.put(
            `${API_END_POINTS.UPDATE_JOB}/${id}`,
            updatedData,
            authHeaders(token)
          );
          set((state) => ({
            profile: res.data,
            userJobs: Array.isArray(state.userJobs)
              ? state.userJobs.map((job) => (job._id === id ? { ...job, ...res.data } : job))
              : state.userJobs,
            isLoading: false,
          }));
        } catch (err: any) {
          set({
            error: err.message || "Failed to update job profile",
            isLoading: false,
          });
          throw err;
        }
      },

      updateJobStatus: async (id, updatedData, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.put(
            `${API_END_POINTS.ARCHIVED_JOB}/${id}`,
            typeof updatedData === "boolean"
              ? { projectvisibility: updatedData }
              : updatedData,
            authHeaders(token)
          );
          set({ isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to update job profile",
            isLoading: false,
          });
          throw err;
        }
      },

      deleteJob: async (id, token) => {
        set({ isLoading: true, error: null });
        try {
          await Axios.delete(
            `${API_END_POINTS.UPDATE_JOB}/${id}`,
            authHeaders(token)
          );
          set((state) => ({
            userJobs: Array.isArray(state.userJobs)
              ? state.userJobs.filter((job) => job._id !== id)
              : state.userJobs,
            isLoading: false,
          }));
        } catch (err: any) {
          set({
            error: err.message || "Failed to delete job",
            isLoading: false,
          });
          throw err;
        }
      },

      applyJob: async (id, data, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.post(
            `${API_END_POINTS.APPLY_JOB}`,
            data,
            authHeaders(token)
          );
          set({ isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to apply job",
            isLoading: false,
          });
          throw err;
        }
      },

      clearJobProfile: () =>
        set({ profile: null, isLoading: false, error: null }),
    }),
    {
      name: "job-storage",
    }
  )
);
